import { useEffect, useState } from 'react';

/**
 * Custom hook for persisting tab selection in URL hash
 * 
 * @param tabs - Object or array of valid tab keys (e.g., { calendar: "calendar", timeline: "timeline" })
 * @param defaultTab - Default tab key to use if hash is invalid or missing
 * @returns [activeTabKey, setActiveTabKey] - Tuple with current tab and setter function
 * 
 * @example
 * const TABS = { calendar: "calendar", timeline: "timeline" } as const;
 * type TabKey = typeof TABS[keyof typeof TABS];
 * const [activeTabKey, setActiveTabKey] = useTabHashPersistence(TABS, TABS.calendar);
 */
export function useTabHashPersistence<T extends string>(
  tabs: Record<string, T> | readonly T[],
  defaultTab: T
): [T, (tab: T) => void] {
  // Get valid tab values
  const validTabs = Array.isArray(tabs) ? tabs : Object.values(tabs);
  
  // Initialize activeTabKey from URL hash, fallback to default
  const getInitialTabFromHash = (): T => {
    if (typeof window !== 'undefined') {
      const hash = window.location.hash.replace('#', '');
      if (hash && validTabs.includes(hash as T)) {
        return hash as T;
      }
    }
    return defaultTab;
  };
  
  const [activeTabKey, setActiveTabKeyState] = useState<T>(getInitialTabFromHash());

  // Update URL hash when tab changes
  const setActiveTabKey = (newTab: T) => {
    setActiveTabKeyState(newTab);
    // Update URL hash to persist tab selection using replaceState to avoid triggering hashchange
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.hash = newTab;
      window.history.replaceState(null, '', url.toString());
    }
  };

  // Listen to hash changes (browser back/forward buttons)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash && validTabs.includes(hash as T)) {
        setActiveTabKeyState(hash as T);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [validTabs]);

  return [activeTabKey, setActiveTabKey];
}

